import React from "react";

import type { CellMouseDownEvent } from "@/ui/molecules/Grid";

import type {
    TExpandableGridContext,
    TRowReviewWithOperatingStatements,
} from "./typesGrids";

export const useExpandableSubcategories = <
    TRow extends TRowReviewWithOperatingStatements,
>(
    rows: TRow[],
): {
    visibleRows: TRow[];
    context: TExpandableGridContext;
    onCellMouseDown: (event: CellMouseDownEvent<TRow>) => void;
} => {
    const [expandedSubcategories, setExpandedSubcategories] = React.useState<
        ReadonlySet<string>
    >(() => new Set());

    const toggleSubcategory = React.useCallback((rowId: number | string) => {
        setExpandedSubcategories(previous => {
            const next = new Set(previous);
            const key = String(rowId);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const visibleRows = React.useMemo(
        () =>
            rows
                .filter(
                    row =>
                        row.parentRowId == null ||
                        expandedSubcategories.has(String(row.parentRowId)),
                )
                .map(row =>
                    row.type === "SUBSECTION"
                        ? {
                              ...row,
                              isExpanded: expandedSubcategories.has(
                                  String(row.rowId),
                              ),
                          }
                        : row,
                ),
        [rows, expandedSubcategories],
    );

    const context = React.useMemo<TExpandableGridContext>(
        () => ({ toggleSubcategory }),
        [toggleSubcategory],
    );

    const onCellMouseDown = React.useCallback(
        (event: CellMouseDownEvent<TRow>) => {
            const isLeftButton =
                !(event.event instanceof MouseEvent) ||
                event.event.button === 0;
            if (
                isLeftButton &&
                event.column.getColId() === "account" &&
                event.data?.type === "SUBSECTION"
            ) {
                toggleSubcategory(event.data.rowId);
            }
        },
        [toggleSubcategory],
    );

    return { visibleRows, context, onCellMouseDown };
};
